import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
} from "recharts";
import moment from "moment";
import Footer from "../common/Footer";
import { BarLoader } from "react-spinners";




const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "rgba(30, 30, 30, 0.95)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          padding: "12px 16px",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
          color: "#fff",
        }}
      >
        <p style={{ margin: "0 0 8px 0", fontWeight: "600", fontSize: "14px", color: "#fff" }}>
          {label}
        </p>
        {payload.map((entry, index) => (
          <p
            key={index}
            style={{
              margin: "4px 0",
              color: entry.color,
              fontSize: "13px",
              fontWeight: "500",
            }}
          >
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Custom Treemap Component
const TreemapChart = ({ data }) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const colors = [
    "#8884d8",
    "#82ca9d",
    "#4db863",
    "#ffc658",
    "#ff7300",
    "#a8a4f0",
    "#92d4ad",
    "#3ca151",
  ];

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const sortedData = [...data].sort((a, b) => b.value - a.value);

  // Simple treemap layout algorithm (squarified)
  const calculateTreemap = (items, width, height) => {
    const result = [];
    let row = [];
    let rowWidth = 0;
    let rowHeight = 0;
    let x = 0;
    let y = 0;

    items.forEach((item, index) => {
      const area = (item.value / total) * width * height;
      const aspectRatio = row.length > 0 
        ? Math.max(rowWidth / rowHeight, rowHeight / rowWidth)
        : Infinity;
      
      const newRow = [...row, item];
      const newRowWidth = rowWidth + (area / height);
      const newRowHeight = Math.max(rowHeight, area / newRowWidth);
      const newAspectRatio = Math.max(newRowWidth / newRowHeight, newRowHeight / newRowWidth);

      if (newAspectRatio < aspectRatio && index < items.length - 1) {
        row = newRow;
        rowWidth = newRowWidth;
        rowHeight = newRowHeight;
      } else {
        let currentX = x;
        row.forEach((rowItem) => {
          const itemArea = (rowItem.value / total) * width * height;
          const itemWidth = itemArea / rowHeight;
          result.push({
            ...rowItem,
            x: currentX,
            y: y,
            width: itemWidth,
            height: rowHeight,
          });
          currentX += itemWidth;
        });
        
        x = 0;
        y += rowHeight;
        row = [item];
        rowWidth = (area / height);
        rowHeight = area / rowWidth;
      }
    });

    let currentX = x;
    row.forEach((rowItem) => {
      const itemArea = (rowItem.value / total) * width * height;
      const itemWidth = itemArea / rowHeight;
      result.push({
        ...rowItem,
        x: currentX,
        y: y,
        width: itemWidth,
        height: rowHeight,
      });
      currentX += itemWidth;
    });

    return result;
  };

  const treemapItems = calculateTreemap(sortedData, 800, 400);

  return (
    <div style={{ width: "100%", height: "500px", position: "relative" }}>
      <svg width="100%" height="100%" viewBox="0 0 800 400" preserveAspectRatio="xMidYMid meet">
        {treemapItems.map((item, index) => {
          const color = colors[index % colors.length];
          const isHovered = hoveredItem === item.name;
          const percentage = ((item.value / total) * 100).toFixed(1);
          
          return (
            <g key={item.name}>
              <rect
                x={item.x}
                y={item.y}
                width={item.width}
                height={item.height}
                fill={color}
                stroke="rgba(255, 255, 255, 0.2)"
                strokeWidth={isHovered ? 2 : 1}
                opacity={isHovered ? 0.9 : 0.8}
                onMouseEnter={() => setHoveredItem(item.name)}
                onMouseLeave={() => setHoveredItem(null)}
                style={{ cursor: "pointer", transition: "all 0.2s" }}
              />
              {item.width > 60 && item.height > 30 && (
                <text
                  x={item.x + item.width / 2}
                  y={item.y + item.height / 2 - 8}
                  textAnchor="middle"
                  fill="#fff"
                  fontSize="12"
                  fontWeight="600"
                  style={{ pointerEvents: "none" }}
                >
                  {item.name}
                </text>
              )}
              {item.width > 60 && item.height > 30 && (
                <text
                  x={item.x + item.width / 2}
                  y={item.y + item.height / 2 + 8}
                  textAnchor="middle"
                  fill="rgba(255, 255, 255, 0.8)"
                  fontSize="11"
                  style={{ pointerEvents: "none" }}
                >
                  {item.value} orgs ({percentage}%)
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hoveredItem && (
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            backgroundColor: "rgba(30, 30, 30, 0.95)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            padding: "12px 16px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            color: "#fff",
            zIndex: 10,
          }}
        >
          <p style={{ margin: "0 0 4px 0", fontWeight: "600", fontSize: "14px" }}>
            {hoveredItem}
          </p>
          <p style={{ margin: "0", fontSize: "12px", color: "rgba(255, 255, 255, 0.8)" }}>
            {sortedData.find(d => d.name === hoveredItem)?.value} organizations
          </p>
        </div>
      )}
    </div>
  );
};




const Demographics = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const userToken = localStorage.getItem("userToken");
  const [interval, setInterval] = useState("weekly");
  const [selectedFilter, setSelectedFilter] = useState("all"); // "all", "donors", "impactOrgs", "nonprofits"
  const [impactOrgsLocationIndustry, setImpactOrgsLocationIndustry] = useState([]);
  const [impactOrgsInflowSupporters, setImpactOrgsInflowSupporters] = useState([]);
  const [nonprofitsLocationCause, setNonprofitsLocationCause] = useState([]);
  const [nonprofitsDonorEngagement, setNonprofitsDonorEngagement] = useState([]);
  const [donorCausesData, setDonorCausesData] = useState([]);
  const [donorCausesLoading, setDonorCausesLoading] = useState(true);
  const [impactOrgsLoading, setImpactOrgsLoading] = useState(true);
  const [nonprofitsLoading, setNonprofitsLoading] = useState(true);
  const [donorActivityData, setDonorActivityData] = useState([]);
  const [donorActivityLoading, setDonorActivityLoading] = useState(true);
  const [donorLocationData, setDonorLocationData] = useState([]);
  const [donorLocationLoading, setDonorLocationLoading] = useState(true);


  const loadData = () => {
    setLoading(true);
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/adminkpi?period=${interval}`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        setData(
          response.data.map((item) => ({
            date: (interval === "yearly" || interval === "6months" || interval === "2years")
              ? item.date // For yearly/6months/2years, date is already a month name string
              : moment(item.date).format("DD-MM-YYYY"), // For weekly/monthly, format as date
            dailyUsers: item.dailyUsers,
            totalCoinsCirculation: item.totalCoinsCirculation?.toFixed(3),
            coinsHoldingByUsers: item.coinsHoldingByUsers.toFixed(3),
            paymentTransactions: item.paymentTransactions,
          }))
        );
        setLoading(false);
      })
      .catch(() => {});
  };

  const handleIntervalChange = (event) => {
    setInterval(event.target.value);
  };

  const handleFilterChange = (event) => {
    setSelectedFilter(event.target.value);
  };

  useEffect(() => {
    loadData();
  }, [interval]);

  const loadImpactOrgsData = () => {
    setImpactOrgsLoading(true);
    Promise.all([
      axios.get(`${process.env.REACT_APP_LOCAL_API}/impact-orgs-location-industry`, {
        headers: { Authorization: `Bearer ${userToken}` }
      }),
      axios.get(`${process.env.REACT_APP_LOCAL_API}/impact-orgs-inflow-supporters`, {
        headers: { Authorization: `Bearer ${userToken}` }
      })
    ])
      .then(([locationRes, inflowRes]) => {
        setImpactOrgsLocationIndustry(locationRes.data);
        setImpactOrgsInflowSupporters(inflowRes.data);
        setImpactOrgsLoading(false);
      })
      .catch((error) => {
        setImpactOrgsLoading(false);
      });
  };

  const loadNonprofitsData = () => {
    setNonprofitsLoading(true);
    Promise.all([
      axios.get(`${process.env.REACT_APP_LOCAL_API}/nonprofits-location-cause`, {
        headers: { Authorization: `Bearer ${userToken}` }
      }),
      axios.get(`${process.env.REACT_APP_LOCAL_API}/nonprofits-donor-engagement`, {
        headers: { Authorization: `Bearer ${userToken}` }
      })
    ])
      .then(([locationRes, engagementRes]) => {
        setNonprofitsLocationCause(locationRes.data);
        setNonprofitsDonorEngagement(engagementRes.data);
        setNonprofitsLoading(false);
      })
      .catch((error) => {
        setNonprofitsLoading(false);
      });
  };

  const loadDonorCausesData = () => {
    setDonorCausesLoading(true);
    axios.get(`${process.env.REACT_APP_LOCAL_API}/donor-causes`, {
      headers: { Authorization: `Bearer ${userToken}` }
    })
      .then((response) => {
        setDonorCausesData(response.data);
        setDonorCausesLoading(false);
      })
      .catch((error) => {
        setDonorCausesLoading(false);
      });
  };

  const loadDonorActivityData = () => {
    setDonorActivityLoading(true);
    axios.get(`${process.env.REACT_APP_LOCAL_API}/donor-activity-trend?period=${interval}`, {
      headers: { Authorization: `Bearer ${userToken}` }
    })
      .then((response) => {
        setDonorActivityData(
          response.data.map((item) => ({
            date: (interval === "yearly" || interval === "6months" || interval === "2years")
              ? item.date // For yearly/6months/2years, date is already a month name string
              : moment(item.date).format("DD-MM-YYYY"), // For weekly/monthly, format as date
            activeDonors: item.activeDonors,
            movingAvg: item.movingAvg
          }))
        );
        setDonorActivityLoading(false);
      })
      .catch((error) => {
        setDonorActivityLoading(false);
      });
  };

  const loadDonorLocationData = () => {
    setDonorLocationLoading(true);
    axios.get(`${process.env.REACT_APP_LOCAL_API}/donor-location-distribution`, {
      headers: { Authorization: `Bearer ${userToken}` }
    })
      .then((response) => {
        setDonorLocationData(response.data);
        setDonorLocationLoading(false);
      })
      .catch((error) => {
        setDonorLocationLoading(false);
      });
  };

  useEffect(() => {
    loadImpactOrgsData();
    loadNonprofitsData();
    loadDonorCausesData();
    loadDonorLocationData();
  }, []);

  useEffect(() => {
    loadDonorActivityData();
  }, [interval]);

  const chartCardStyle = {
    backgroundColor: "rgba(30, 30, 30, 0.6)",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "30px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
  };

  const chartTitleStyle = {
    color: "#fff",
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "20px",
    paddingBottom: "12px",
    borderBottom: "2px solid rgba(255, 255, 255, 0.1)",
  };

  // Calculate max value for real data Total DM in Circulation Y-axis
  const realDataMaxCirculation = data.length > 0 
    ? Math.max(...data.map(item => parseFloat(item.totalCoinsCirculation) || 0))
    : 0;
  const realDataYAxisMax = Math.ceil(realDataMaxCirculation * 1.2);
  
  // When all values are 0, ensure domain shows 0 properly with visible horizontal line
  const isAllZero = realDataMaxCirculation === 0;


  const getGradientId = (category) => {
    const gradientMap = {
      Social: "colorSocial",
      Environmental: "colorEnvironmental",
      Emergency: "colorEmergency",
      Cultural: "colorCultural",
      Innovation: "colorInnovation",
    };
    return gradientMap[category] || "colorSocial";
  };

  return (
    <>     
    <div style={{ width: "95%", margin: "50px auto", paddingBottom: "20px" }}>
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: "30px",
        flexWrap: "wrap",
        gap: "20px"
      }}>
        <h1 style={{ 
          color: "white", 
          fontSize: "32px",
          fontWeight: "700",
          margin: 0,
        }}>
          Analytics Dashboard
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" }}>
          <select 
            value={interval} 
            onChange={handleIntervalChange}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backgroundColor: "rgba(30, 30, 30, 0.8)",
              color: "#fff",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              outline: "none",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = "rgba(255, 255, 255, 0.4)";
              e.target.style.backgroundColor = "rgba(40, 40, 40, 0.9)";
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = "rgba(255, 255, 255, 0.2)";
              e.target.style.backgroundColor = "rgba(30, 30, 30, 0.8)";
            }}
          >
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
          <option value="2years">2 Years</option>
        </select>
          <select 
            value={selectedFilter} 
            onChange={handleFilterChange}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "1px solid rgba(60, 161, 81, 0.5)",
              backgroundColor: "rgba(30, 30, 30, 0.8)",
              color: "#fff",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              outline: "none",
              transition: "all 0.3s ease",
              minWidth: "200px",
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = "rgba(60, 161, 81, 0.8)";
              e.target.style.backgroundColor = "rgba(40, 40, 40, 0.9)";
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = "rgba(60, 161, 81, 0.5)";
              e.target.style.backgroundColor = "rgba(30, 30, 30, 0.8)";
            }}
          >
          <option value="all">All Sections</option>
          <option value="donors">Donors Overview</option>
          <option value="impactOrgs">Impact Organizations</option>
          <option value="nonprofits">Nonprofits</option>
        </select>
          {loading && (
            <div style={{ display: "flex", alignItems: "center" }}>
              <BarLoader color="#3ca151" width={100} />
            </div>
          )}
        </div>
      </div>

      {/* General Charts - Only show when "All Sections" is selected */}
      {selectedFilter === "all" && (
      <>
      {/* Daily Users Chart */}
      <div style={chartCardStyle}>
        <h3 style={chartTitleStyle}>Daily Users</h3>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart
            data={data}
            margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
            <XAxis 
              dataKey="date" 
              stroke="#fff"
              style={{ fontSize: "12px" }}
              tick={{ fill: "#fff" }}
            />
            <YAxis 
              stroke="#fff"
              style={{ fontSize: "12px" }}
              tick={{ fill: "#fff" }}
              domain={[0, 'dataMax']}
              allowDataOverflow={false}
              padding={{ top: 20 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ color: "#fff", paddingTop: "20px" }}
              iconType="line"
            />
            <Area
              type="monotone"
              dataKey="dailyUsers"
              stroke="#8884d8"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorUsers)"
              name="Daily Users"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Total DM in Circulation Chart */}
      <div style={{ ...chartCardStyle, overflow: "hidden" }}>
        <h3 style={chartTitleStyle}>Total DM in Circulation</h3>
        <ResponsiveContainer width="100%" height={450}>
            <AreaChart
              data={data}
              margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorCirculation" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis 
                dataKey="date" 
                stroke="#fff"
                style={{ fontSize: "12px" }}
                tick={{ fill: "#fff" }}
              />
              <YAxis 
                stroke="#fff"
                style={{ fontSize: "12px" }}
                tick={{ fill: "#fff" }}
                domain={isAllZero ? [-100, 100] : [0, realDataYAxisMax || 'dataMax']}
                allowDataOverflow={false}
                padding={isAllZero ? { top: 0, bottom: 0 } : { top: 20 }}
                tickCount={isAllZero ? 1 : undefined}
                ticks={isAllZero ? [0] : undefined}
              />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ color: "#fff", paddingTop: "20px" }}
              iconType="line"
            />
            <Area
              type="monotone"
              dataKey="totalCoinsCirculation"
              stroke="#82ca9d"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorCirculation)"
              name="Total DM in Circulation"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* DM Holding By Users Chart */}
      <div style={chartCardStyle}>
        <h3 style={chartTitleStyle}>DM Holding By Users</h3>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart
            data={data}
            margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorHolding" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ffc658" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#ffc658" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
            <XAxis 
              dataKey="date" 
              stroke="#fff"
              style={{ fontSize: "12px" }}
              tick={{ fill: "#fff" }}
            />
            <YAxis 
              stroke="#fff"
              style={{ fontSize: "12px" }}
              tick={{ fill: "#fff" }}
              domain={[0, 'dataMax']}
              allowDataOverflow={false}
              padding={{ top: 20 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ color: "#fff", paddingTop: "20px" }}
              iconType="line"
            />
            <Area
              type="monotone"
              dataKey="coinsHoldingByUsers"
              stroke="#ffc658"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorHolding)"
              name="DM Holding By Users"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Payment Transactions Chart */}
      <div style={chartCardStyle}>
        <h3 style={chartTitleStyle}>Payment Transactions</h3>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart
            data={data}
            margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorTransactions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ff7300" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#ff7300" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
            <XAxis 
              dataKey="date" 
              stroke="#fff"
              style={{ fontSize: "12px" }}
              tick={{ fill: "#fff" }}
            />
            <YAxis 
              stroke="#fff"
              style={{ fontSize: "12px" }}
              tick={{ fill: "#fff" }}
              domain={[0, 'dataMax']}
              allowDataOverflow={false}
              padding={{ top: 20 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ color: "#fff", paddingTop: "20px" }}
              iconType="line"
            />
            <Area
              type="monotone"
              dataKey="paymentTransactions"
              stroke="#ff7300"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorTransactions)"
              name="No. Payment Transactions"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      </>
      )}

      {/* Donors Overview Section */}
      {(selectedFilter === "all" || selectedFilter === "donors") && (
      <div style={{ marginTop: "50px", width: "100%" }}>
        <h2 style={{ 
          color: "white", 
          fontSize: "28px", 
          fontWeight: "700", 
          marginBottom: "30px",
          textAlign: "left"
        }}>
          Donors Overview
        </h2>

        <div style={{ ...chartCardStyle, overflow: "hidden" }}>
          <h3 style={chartTitleStyle}>Donor Location Distribution</h3>
          {donorLocationLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
              <BarLoader color="#3ca151" width={100} />
            </div>
          ) : donorLocationData.length > 0 ? (
          <ResponsiveContainer width="100%" height={450}>
            <BarChart
              data={donorLocationData}
              margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              layout="vertical"
            >
              <defs>
                <linearGradient id="colorBarChart" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#4db863" stopOpacity={0.9}/>
                  <stop offset="50%" stopColor="#3ca151" stopOpacity={0.8}/>
                  <stop offset="100%" stopColor="#2d7a3e" stopOpacity={0.3}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis 
                type="number"
                stroke="#fff"
                style={{ fontSize: "12px" }}
                tick={{ fill: "#fff" }}
                domain={[0, 'dataMax']}
              />
              <YAxis 
                type="category"
                dataKey="location"
                stroke="#fff"
                style={{ fontSize: "12px" }}
                tick={{ fill: "#fff" }}
                width={60}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="donors" 
                fill="url(#colorBarChart)"
                radius={[0, 4, 4, 0]}
              >
                {donorLocationData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill="url(#colorBarChart)" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", padding: "40px", color: "#fff" }}>
              No donor location data available
            </div>
          )}
        </div>

        {/* 2. Donor Causes - Minimal 3D Pie Chart */}
        <div style={{ ...chartCardStyle, overflow: "visible", position: "relative" }}>
          <h3 style={chartTitleStyle}>Donor Causes</h3>
          {donorCausesLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
              <BarLoader color="#3ca151" width={100} />
            </div>
          ) : donorCausesData.length > 0 ? (
          <ResponsiveContainer width="100%" height={500}>
            <PieChart>
              <defs>
                <filter id="minimal3DShadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
                  <feOffset dx="0" dy="2" result="offsetblur"/>
                  <feComponentTransfer>
                    <feFuncA type="linear" slope="0.25"/>
                  </feComponentTransfer>
                  <feMerge>
                    <feMergeNode/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
                <linearGradient id="colorCause1" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#9b98e8" stopOpacity={1}/>
                  <stop offset="50%" stopColor="#8884d8" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#6b67b8" stopOpacity={1}/>
                </linearGradient>
                <linearGradient id="colorCause2" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#a2d4b5" stopOpacity={1}/>
                  <stop offset="50%" stopColor="#82ca9d" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#5a9d75" stopOpacity={1}/>
                </linearGradient>
                <linearGradient id="colorCause3" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#4db863" stopOpacity={1}/>
                  <stop offset="50%" stopColor="#3ca151" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#2d7a3e" stopOpacity={1}/>
                </linearGradient>
                <linearGradient id="colorCause4" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#ffd678" stopOpacity={1}/>
                  <stop offset="50%" stopColor="#ffc658" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#cc9e46" stopOpacity={1}/>
                </linearGradient>
                <linearGradient id="colorCause5" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#ff8333" stopOpacity={1}/>
                  <stop offset="50%" stopColor="#ff7300" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#cc5a00" stopOpacity={1}/>
                </linearGradient>
                <linearGradient id="colorCause6" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#a8a4f0" stopOpacity={1}/>
                  <stop offset="50%" stopColor="#8884d8" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#6361a3" stopOpacity={1}/>
                </linearGradient>
                <linearGradient id="colorCause7" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#92d4ad" stopOpacity={1}/>
                  <stop offset="50%" stopColor="#82ca9d" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#5a9d75" stopOpacity={1}/>
                </linearGradient>
                <linearGradient id="colorCause8" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#4db863" stopOpacity={1}/>
                  <stop offset="50%" stopColor="#3ca151" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#2d7a3e" stopOpacity={1}/>
                </linearGradient>
              </defs>
              <Pie
                data={donorCausesData}
                cx="50%"
                cy="51%"
                outerRadius={140}
                innerRadius={0}
                fill="none"
                dataKey="donors"
                paddingAngle={1}
                stroke="none"
                filter="url(#minimal3DShadow)"
                opacity={0.1}
              >
                {donorCausesData.map((entry, index) => {
                  const colorIds = ["colorCause1", "colorCause2", "colorCause3", "colorCause4", "colorCause5", "colorCause6", "colorCause7", "colorCause8"];
                  const gradientId = colorIds[index % colorIds.length];
                  return (
                    <Cell key={`shadow-${index}`} fill={`url(#${gradientId})`} />
                  );
                })}
              </Pie>
              <Pie
                data={donorCausesData}
                cx="50%"
                cy="50%"
                labelLine={(props) => {
                  const { cx, cy, midAngle, outerRadius, percent } = props;
                  const RADIAN = Math.PI / 180;
                  const pullOutDistance = percent < 0.05 ? 6 : 0;
                  const startRadius = outerRadius + pullOutDistance;
                  const endRadius = outerRadius + 30 + pullOutDistance;
                  
                  const x1 = cx + startRadius * Math.cos(-midAngle * RADIAN);
                  const y1 = cy + startRadius * Math.sin(-midAngle * RADIAN);
                  const x2 = cx + endRadius * Math.cos(-midAngle * RADIAN);
                  const y2 = cy + endRadius * Math.sin(-midAngle * RADIAN);
                  
                  return (
                    <line
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="rgba(255, 255, 255, 0.4)"
                      strokeWidth={1}
                    />
                  );
                }}
                label={(props) => {
                  const { cx, cy, midAngle, outerRadius, name, percent } = props;
                  const RADIAN = Math.PI / 180;
                  const pullOutDistance = percent < 0.05 ? 6 : 0;
                  const radius = outerRadius + 45 + pullOutDistance;
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);
                  
                  return (
                    <text
                      x={x}
                      y={y}
                      fill="#ffffff"
                      textAnchor={x > cx ? 'start' : 'end'}
                      dominantBaseline="central"
                      style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        textShadow: '0 1px 2px rgba(0,0,0,0.7)'
                      }}
                    >
                      {`${name}: ${(percent * 100).toFixed(1)}%`}
                    </text>
                  );
                }}
                outerRadius={(entry, index) => {
                  const total = donorCausesData.reduce((sum, item) => sum + item.donors, 0);
                  const percent = entry.donors / total;
                  return percent < 0.05 ? 146 : 140;
                }}
                innerRadius={0}
                fill="#8884d8"
                dataKey="donors"
                paddingAngle={1}
                stroke="rgba(255, 255, 255, 0.08)"
                strokeWidth={0.5}
                filter="url(#minimal3DShadow)"
              >
                {donorCausesData.map((entry, index) => {
                  const colorIds = ["colorCause1", "colorCause2", "colorCause3", "colorCause4", "colorCause5", "colorCause6", "colorCause7", "colorCause8"];
                  const gradientId = colorIds[index % colorIds.length];
                  return (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={`url(#${gradientId})`}
                    />
                  );
                })}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: "rgba(20, 20, 20, 0.95)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "6px",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                  padding: "10px 14px",
                  fontSize: "12px"
                }}
                formatter={(value, name, props) => {
                  const total = donorCausesData.reduce((sum, item) => sum + item.donors, 0);
                  const percentage = ((value / total) * 100).toFixed(1);
                  return [
                    <div key="tooltip" style={{ lineHeight: "1.5" }}>
                      <div style={{ 
                        fontWeight: "600", 
                        marginBottom: "4px", 
                        fontSize: "14px",
                        color: "#fff"
                      }}>
                        {props.payload.name}
                      </div>
                      <div style={{ 
                        marginBottom: "2px",
                        color: "rgba(255, 255, 255, 0.9)"
                      }}>
                        <strong style={{ color: "#fff" }}>{percentage}%</strong>
                      </div>
                      <div style={{ 
                        fontSize: "11px", 
                        color: "rgba(255, 255, 255, 0.6)"
                      }}>
                        {value.toLocaleString()} donors
                      </div>
                    </div>
                  ];
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", padding: "40px", color: "#fff" }}>
              No donor causes data available
            </div>
          )}
        </div>

        <div style={{ ...chartCardStyle, overflow: "hidden" }}>
          <h3 style={chartTitleStyle}>Donor Activity Trend</h3>
          {donorActivityLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
              <BarLoader color="#3ca151" width={100} />
            </div>
          ) : donorActivityData.length > 0 ? (
          <ResponsiveContainer width="100%" height={450}>
            <AreaChart
              data={donorActivityData}
              margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorActiveDonors" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#9b98e8" stopOpacity={0.9}/>
                  <stop offset="50%" stopColor="#8884d8" stopOpacity={0.8}/>
                  <stop offset="100%" stopColor="#6b67b8" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorMovingAvg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4db863" stopOpacity={0.9}/>
                  <stop offset="50%" stopColor="#3ca151" stopOpacity={0.8}/>
                  <stop offset="100%" stopColor="#2d7a3e" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis 
                dataKey="date"
                stroke="#fff"
                style={{ fontSize: "12px" }}
                tick={{ fill: "#fff" }}
              />
              <YAxis 
                stroke="#fff"
                style={{ fontSize: "12px" }}
                tick={{ fill: "#fff" }}
                domain={[0, 'dataMax']}
                allowDataOverflow={false}
                padding={{ top: 20 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ color: "#fff", paddingTop: "20px" }}
                iconType="line"
              />
              <Area
                type="monotone"
                dataKey="activeDonors"
                stroke="#8884d8"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorActiveDonors)"
                name="Active Donors"
              />
              <Area
                type="monotone"
                dataKey="movingAvg"
                stroke="#3ca151"
                strokeWidth={2}
                strokeDasharray="5 5"
                fillOpacity={0.3}
                fill="url(#colorMovingAvg)"
                name="Moving Average"
              />
            </AreaChart>
          </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", padding: "40px", color: "#fff" }}>
              No donor activity data available
            </div>
          )}
        </div>
      </div>
      )}

      {/* Impact Organizations Section */}
      {(selectedFilter === "all" || selectedFilter === "impactOrgs") && (
      <div style={{ marginTop: "50px", width: "100%" }}>
        <h2 style={{ 
          color: "white", 
          fontSize: "28px", 
          fontWeight: "700", 
          marginBottom: "30px",
          textAlign: "left"
        }}>
          Impact Organizations
        </h2>

        {/* Graph 1: Location & Industry Distribution */}
        <div style={{ ...chartCardStyle, overflow: "hidden" }}>
          <h3 style={chartTitleStyle}>Location & Industry Distribution</h3>
          {impactOrgsLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
              <BarLoader color="#3ca151" width={100} />
            </div>
          ) : impactOrgsLocationIndustry.length > 0 ? (
            <ResponsiveContainer width="100%" height={450}>
              <BarChart
                data={impactOrgsLocationIndustry}
                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorEducation" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorHealthcare" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorEnvironment" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4db863" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#4db863" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorTechnology" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ffc658" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#ffc658" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorSocialServices" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff7300" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#ff7300" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorAnimalWelfare" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#a8a4f0" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#a8a4f0" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorArtsCulture" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#92d4ad" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#92d4ad" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorHumanRights" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4db863" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#4db863" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorWaterSanitation" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorDisasterRelief" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff7300" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#ff7300" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorOther" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.5}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0.3}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                <XAxis 
                  dataKey="location" 
                  stroke="#fff"
                  style={{ fontSize: "12px" }}
                  tick={{ fill: "#fff" }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#fff"
                  style={{ fontSize: "12px" }}
                  tick={{ fill: "#fff" }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="Education" stackId="a" fill="url(#colorEducation)" />
                <Bar dataKey="Healthcare" stackId="a" fill="url(#colorHealthcare)" />
                <Bar dataKey="Environment" stackId="a" fill="url(#colorEnvironment)" />
                <Bar dataKey="Technology" stackId="a" fill="url(#colorTechnology)" />
                <Bar dataKey="Social Services" stackId="a" fill="url(#colorSocialServices)" />
                <Bar dataKey="Animal Welfare" stackId="a" fill="url(#colorAnimalWelfare)" />
                <Bar dataKey="Arts & Culture" stackId="a" fill="url(#colorArtsCulture)" />
                <Bar dataKey="Human Rights" stackId="a" fill="url(#colorHumanRights)" />
                <Bar dataKey="Water & Sanitation" stackId="a" fill="url(#colorWaterSanitation)" />
                <Bar dataKey="Disaster Relief" stackId="a" fill="url(#colorDisasterRelief)" />
                <Bar dataKey="Other" stackId="a" fill="url(#colorOther)" />
                <Legend 
                  wrapperStyle={{ color: "#fff", paddingTop: "20px" }}
                  iconType="line"
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", padding: "40px", color: "#fff" }}>
              No impact organizations data available
            </div>
          )}
        </div>

        {/* Graph 2: DM Inflow & Supporters Overview */}
        <div style={{ ...chartCardStyle, overflow: "hidden" }}>
          <h3 style={chartTitleStyle}>DM Inflow & Supporters Overview</h3>
          {impactOrgsLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
              <BarLoader color="#3ca151" width={100} />
            </div>
          ) : impactOrgsInflowSupporters.length > 0 ? (
            <ResponsiveContainer width="100%" height={450}>
              <ComposedChart
                data={impactOrgsInflowSupporters}
                margin={{ top: 20, right: 30, left: 20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorDMInflow" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4db863" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#3ca151" stopOpacity={0.6}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                <XAxis 
                  dataKey="name" 
                  stroke="#fff"
                  style={{ fontSize: "12px" }}
                  tick={{ fill: "#fff" }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  yAxisId="left"
                  stroke="#fff"
                  style={{ fontSize: "12px" }}
                  tick={{ fill: "#fff" }}
                  label={{ value: "DM Inflow", angle: -90, position: "insideLeft", offset: 10, style: { textAnchor: "middle", fill: "#fff" } }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  stroke="#8884d8"
                  style={{ fontSize: "12px" }}
                  tick={{ fill: "#8884d8" }}
                  label={{ value: "Supporters", angle: 90, position: "insideRight", style: { textAnchor: "middle", fill: "#8884d8" } }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  yAxisId="left"
                  dataKey="dmInflow" 
                  fill="url(#colorDMInflow)"
                  name="DM Inflow"
                  radius={[4, 4, 0, 0]}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="supporters" 
                  stroke="#8884d8" 
                  strokeWidth={3}
                  dot={{ fill: "#8884d8", r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Supporters"
                />
                <Legend 
                  wrapperStyle={{ color: "#fff", paddingTop: "20px" }}
                  iconType="line"
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", padding: "40px", color: "#fff" }}>
              No inflow data available
            </div>
          )}
        </div>
      </div>
      )}

      {/* Nonprofits Section */}
      {(selectedFilter === "all" || selectedFilter === "nonprofits") && (
      <div style={{ marginTop: "50px", width: "100%" }}>
        <h2 style={{ 
          color: "white", 
          fontSize: "28px", 
          fontWeight: "700", 
          marginBottom: "30px",
          textAlign: "left"
        }}>
          Nonprofits
        </h2>

        {/* Graph 1: Location & Cause Distribution */}
        <div style={{ ...chartCardStyle, overflow: "hidden" }}>
          <h3 style={chartTitleStyle}>Location & Cause Distribution</h3>
          {nonprofitsLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
              <BarLoader color="#3ca151" width={100} />
            </div>
          ) : nonprofitsLocationCause.length > 0 ? (
            <ResponsiveContainer width="100%" height={450}>
              <BarChart
                data={nonprofitsLocationCause}
                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorCauseEdu" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorCauseHealth" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorCauseEnv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4db863" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#4db863" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorCausePoverty" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ffc658" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#ffc658" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorCauseAnimal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff7300" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#ff7300" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorCauseDisaster" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#a8a4f0" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#a8a4f0" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorCauseArts" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#92d4ad" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#92d4ad" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorCauseTech" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4db863" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#4db863" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorCauseRights" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorCauseWater" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff7300" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#ff7300" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorCauseOther" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.5}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0.3}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                <XAxis 
                  dataKey="location" 
                  stroke="#fff"
                  style={{ fontSize: "12px" }}
                  tick={{ fill: "#fff" }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#fff"
                  style={{ fontSize: "12px" }}
                  tick={{ fill: "#fff" }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="Education" fill="url(#colorCauseEdu)" />
                <Bar dataKey="Healthcare" fill="url(#colorCauseHealth)" />
                <Bar dataKey="Environment" fill="url(#colorCauseEnv)" />
                <Bar dataKey="Poverty Alleviation" fill="url(#colorCausePoverty)" />
                <Bar dataKey="Animal Welfare" fill="url(#colorCauseAnimal)" />
                <Bar dataKey="Disaster Relief" fill="url(#colorCauseDisaster)" />
                <Bar dataKey="Arts & Culture" fill="url(#colorCauseArts)" />
                <Bar dataKey="Technology" fill="url(#colorCauseTech)" />
                <Bar dataKey="Human Rights" fill="url(#colorCauseRights)" />
                <Bar dataKey="Water & Sanitation" fill="url(#colorCauseWater)" />
                <Bar dataKey="Other" fill="url(#colorCauseOther)" />
                <Legend 
                  wrapperStyle={{ color: "#fff", paddingTop: "20px" }}
                  iconType="line"
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", padding: "40px", color: "#fff" }}>
              No nonprofits data available
            </div>
          )}
        </div>

        {/* Graph 2: DM Received & Donor Engagement */}
        <div style={{ ...chartCardStyle, overflow: "hidden" }}>
          <h3 style={chartTitleStyle}>DM Received & Donor Engagement</h3>
          {nonprofitsLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
              <BarLoader color="#3ca151" width={100} />
            </div>
          ) : nonprofitsDonorEngagement.length > 0 ? (
            <ResponsiveContainer width="100%" height={450}>
              <ComposedChart
                data={nonprofitsDonorEngagement}
                margin={{ top: 20, right: 30, left: 20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorDMReceived" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorActiveDonors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4db863" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#3ca151" stopOpacity={0.6}/>
                  </linearGradient>
                  <linearGradient id="colorLapsedDonors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff7300" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#ff7300" stopOpacity={0.6}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                <XAxis 
                  dataKey="name" 
                  stroke="#fff"
                  style={{ fontSize: "12px" }}
                  tick={{ fill: "#fff" }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  yAxisId="left"
                  stroke="#fff"
                  style={{ fontSize: "12px" }}
                  tick={{ fill: "#fff" }}
                  label={{ value: "DM Received", angle: -90, position: "insideLeft", offset: 10, style: { textAnchor: "middle", fill: "#fff" } }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  stroke="#8884d8"
                  style={{ fontSize: "12px" }}
                  tick={{ fill: "#8884d8" }}
                  label={{ value: "Donors", angle: 90, position: "insideRight", style: { textAnchor: "middle", fill: "#8884d8" } }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  yAxisId="left"
                  dataKey="dmReceived" 
                  fill="url(#colorDMReceived)"
                  name="DM Received"
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  yAxisId="right"
                  dataKey="activeDonors" 
                  stackId="a"
                  fill="url(#colorActiveDonors)"
                  name="Active Donors"
                />
                <Bar 
                  yAxisId="right"
                  dataKey="lapsedDonors" 
                  stackId="a"
                  fill="url(#colorLapsedDonors)"
                  name="Lapsed Donors"
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="totalDonors" 
                  stroke="#8884d8" 
                  strokeWidth={3}
                  dot={{ fill: "#8884d8", r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Total Donors"
                />
                <Legend 
                  wrapperStyle={{ color: "#fff", paddingTop: "20px" }}
                  iconType="line"
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", padding: "40px", color: "#fff" }}>
              No donor engagement data available
            </div>
          )}
        </div>
      </div>
      )}
    </div>
    <Footer />
    </>
  );
};

export default Demographics;
